/**
 * T036/T050/T123: Header Component
 * Application header with user menu and mobile menu toggle
 */

import { Link } from '@tanstack/react-router'
import { LogOut, Menu, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useSignOut } from '@/hooks/useSignOut'
import { useUIStore } from '@/stores/ui'

export function Header() {
  const { user, isAuthenticated } = useAuth()
  const { toggleMobileMenu } = useUIStore()

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4">
        {/* Mobile menu toggle - only visible on mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="mr-2 min-h-[44px] min-w-[44px] md:hidden"
          onClick={toggleMobileMenu}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <Link to="/" className="flex items-center gap-2 font-semibold">
          <span className="text-xl">Arcanum</span>
        </Link>

        <div className="flex-1" />

        {isAuthenticated && user ? (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="hidden sm:inline">{user.name || user.email}</span>
            </div>
            <SignOutButton />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" asChild className="min-h-[44px]">
              <Link to="/sign-in">Sign in</Link>
            </Button>
            <Button asChild className="min-h-[44px]">
              <Link to="/sign-up">Sign up</Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}

function SignOutButton() {
  const signOut = useSignOut()

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => signOut.mutate()}
      disabled={signOut.isPending}
      className="gap-2"
      aria-label="Sign out"
    >
      <LogOut className="h-4 w-4" />
      <span className="hidden sm:inline">
        {signOut.isPending ? 'Signing out...' : 'Sign out'}
      </span>
    </Button>
  )
}
